<template>
  <v-card
    class="mb-4"
  >
    <div class="d-flex justify-space-between pa-4 pb-0">
      <v-card-title class="small-title">Budget Tracker</v-card-title>
      <div class="font-weight-light grey--text">Total records: {{ budgetData.length }}</div>
    </div>
    <v-data-table
      class="sow-type-table align-top"
      item-key="task"
      color="primary"
      fixed-header
      disable-pagination
      hide-default-footer
      :items-per-page="5"
      :headers="currentBudgetHeaders"
      :items="flattenedData"
      :dense="denseTables"
      hide-default-header
    >
      <template #header="{ props }">
        <thead>
          <tr>
            <th
              v-for="header in props.headers"
              :key="header.text"
              class="sow-type-table__header"
            >
              {{ header.text }}
            </th>
          </tr>
        </thead>
      </template>

      <template #item="{ item }">
        <BudgetTrackerTableRow :item="item" :headers="currentBudgetHeaders" />
      </template>

      <template #body.append>
        <tr class="total-background">
          <td>Total:</td>
          <td></td>
          <td></td>
          <td></td>
          <td>{{ total.estimation_estimate }}h</td>
          <td>{{ (total.total / 60).toFixed(2) }}h</td>
          <td>{{ total.total_cost }}</td>
        </tr>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import BudgetTrackerTableRow from './BudgetTrackerTableRow.vue';
import apiClient from '@/services/api-client';

/**
 * @typedef {Object} Estimation
 * @property {number} estimation_estimate
 * @property {string} estimation_role
 * @property {Array<TimeEntry>} time_entries
 * @property {number} total_minutes
 * @property {number} estimated_cost_for_customer
 */

/**
 * @typedef {Object} TimeEntry
 * @property {string} time_unit_id
 * @property {number} time_unit_minutes
 * @property {string} time_unit_description
 * @property {number} time_unit_cost_for_customer
 * @property {string} employee_id
 */

/**
 * @typedef {Object} FlattenedItem
 * @property {string} task
 * @property {string} role
 * @property {string} description
 * @property {string} employee
 * @property {number} estimation
 * @property {number} total
 * @property {number} cost
 * @property {boolean} isSubtotal
 * @property {boolean} isTaskStart
 * @property {boolean} isRoleStart
 * @property {number|undefined} taskRowspan
 * @property {number|undefined} roleRowspan
 */

/**
 * @typedef {Object} BudgetData
 * @property {string} task
 * @property {Array<Estimation>} estimations
 * @property {Object} subtotal
 * @property {number} subtotal.estimation_estimate
 * @property {number} subtotal.total
 * @property {number} subtotal.total_cost
 */

/**
 * @typedef {Object} Total
 * @property {number} estimation_estimate
 * @property {number} total
 * @property {number} total_cost
 */

export default {
  name: 'BudgetTrackerTable',
  components: {
    BudgetTrackerTableRow,
  },
  props: {
    budgetData: {
      type: Array,
      default: () => ([]),
    },
  },
  async created() {
    // Получаем всех сотрудников
    const { data: employees } = await apiClient.employeeApi.getEmployees();
    this.employeesMap = employees.reduce((map, emp) => {
      map[emp.id] = emp;
      return map;
    }, {});
  },
  data() {
    return {
      summaryReportUnitToDelete: {},
      isConfirmationDialogOpened: false,
      isRegenerateConfirmationDialogOpen: false,
      assignmentToRegenerate: {},
      employeeNameToRegenerate: {},
      /** @type {Record<string, { id: string, name: string }>} */
      employeesMap: {},
    };
  },
  computed: {
    ...mapGetters('auth/account', ['user']),
    ...mapGetters('user/settings', ['denseTables']),
    ...mapGetters('reports/main', ['report', 'isReportEditable']),
    ...mapGetters('reports/summary', ['summaryMap']),

    currentBudgetHeaders() {
      return [
        {
          text: 'Task',
          value: 'task',
          class: 'sow-type-table__header-task',
        },
        {
          text: 'Role',
          value: 'role',
          class: 'sow-type-table__header-role',
        },
        {
          text: 'Description',
          value: 'description',
          class: 'sow-type-table__header-description',
        },
        {
          text: 'Employee',
          value: 'employee',
          class: 'sow-type-table__header-employee',
        },
        {
          text: 'Estimation (hour)',
          value: 'estimation',
          class: 'sow-type-table__header-estimation',
        },
        {
          text: 'Total (hour)',
          value: 'total',
          class: 'sow-type-table__header-total',
        },
        {
          text: 'Cost for Customer',
          value: 'cost',
          class: 'sow-type-table__header-cost',
        },
      ];
    },

    flattenedData() {
      /** @type {FlattenedItem[]} */
      const result = [];
      
      this.budgetData.forEach((/** @type {BudgetData} */ task) => {
        const taskRows = this.processTask(task);
        result.push(...taskRows);
      });

      return result;
    },

    total() {
      if (!this.budgetData.length) {
        return {
          estimation_estimate: 0,
          total: 0,
          total_cost: 0,
        };
      }

      return this.budgetData.reduce((/** @type {Total} */ acc, /** @type {BudgetData} */ item) => {
        acc.estimation_estimate += item.subtotal.estimation_estimate || 0;
        acc.total += item.subtotal.total || 0;
        acc.total_cost += item.subtotal.total_cost || 0;
        return acc;
      }, {
        estimation_estimate: 0,
        total: 0,
        total_cost: 0,
      });
    },
  },
  methods: {
    ...mapActions('reports/summary', ['deleteSummaryReportUnit', 'getSummary']),
    ...mapActions('reports/units', ['getReportUnits']),

    processTask(/** @type {BudgetData} */ task) {
      /** @type {FlattenedItem[]} */
      const rows = [];
      let isFirstRowForTask = true;
      const taskRowCount = this.calculateTaskRowCount(task);
      
      // Фильтруем оценки, оставляя только те, у которых есть time_entries или estimation_estimate
      const validEstimations = task.estimations.filter(estimation => 
        estimation.time_entries.length > 0 || estimation.estimation_estimate
      );

      // Группируем оценки по ролям
      /** @type {Object.<string, Array<Estimation>>} */
      const roleGroups = {};
      validEstimations.forEach((/** @type {Estimation} */ estimation) => {
        if (!roleGroups[estimation.estimation_role]) {
          roleGroups[estimation.estimation_role] = [];
        }
        roleGroups[estimation.estimation_role].push(estimation);
      });

      // Обрабатываем каждую группу ролей
      Object.entries(roleGroups).forEach(([role, estimations]) => {
        let isFirstRowForRole = true;
        const roleRowCount = estimations.reduce((count, estimation) => 
          count + (estimation.time_entries.length || (estimation.estimation_estimate ? 1 : 0)), 0);

        estimations.forEach((/** @type {Estimation} */ estimation) => {
          if (estimation.time_entries.length > 0) {
            estimation.time_entries.forEach((/** @type {TimeEntry} */ entry) => {
              rows.push(this.createTimeEntryRow(entry, estimation, task.task, isFirstRowForTask, isFirstRowForRole, taskRowCount, roleRowCount));
              isFirstRowForTask = false;
              isFirstRowForRole = false;
            });
          } else if (estimation.estimation_estimate) {
            rows.push(this.createEstimationRow(estimation, task.task, isFirstRowForTask, isFirstRowForRole, taskRowCount, roleRowCount));
            isFirstRowForTask = false;
            isFirstRowForRole = false;
          }
        });
      });

      if (rows.length === 0) {
        rows.push(this.createEmptyTaskRow(task.task, taskRowCount));
      }

      rows.push(this.createSubtotalRow(task.subtotal));

      return rows;
    },

    calculateTaskRowCount(/** @type {BudgetData} */ task) {
      let count = 0;
      let hasEntries = false;

      task.estimations.forEach((/** @type {Estimation} */ estimation) => {
        if (estimation.time_entries.length > 0) {
          hasEntries = true;
          count += estimation.time_entries.length;
        } else if (estimation.estimation_estimate) {
          hasEntries = true;
          count += 1;
        }
      });

      return hasEntries ? count : 1;
    },

    createTimeEntryRow(/** @type {TimeEntry} */ entry, /** @type {Estimation} */ estimation, /** @type {string} */ taskName, /** @type {boolean} */ isFirstRowForTask, /** @type {boolean} */ isFirstRowForRole, /** @type {number} */ taskRowCount, /** @type {number} */ roleRowCount) {
      const employeeName = entry.employee_id && this.employeesMap[entry.employee_id] 
        ? this.employeesMap[entry.employee_id].name 
        : 'Unknown';
      
      return {
        task: taskName,
        role: estimation.estimation_role || '',
        description: entry.time_unit_description,
        employee: employeeName,
        estimation: estimation.estimation_estimate || 0,
        total: entry.time_unit_minutes,
        cost: entry.time_unit_cost_for_customer,
        isSubtotal: false,
        isTaskStart: isFirstRowForTask,
        isRoleStart: isFirstRowForRole,
        taskRowspan: isFirstRowForTask ? taskRowCount : undefined,
        roleRowspan: isFirstRowForRole ? roleRowCount : undefined
      };
    },

    createEstimationRow(/** @type {Estimation} */ estimation, /** @type {string} */ taskName, /** @type {boolean} */ isFirstRowForTask, /** @type {boolean} */ isFirstRowForRole, /** @type {number} */ taskRowCount, /** @type {number} */ roleRowCount) {
      return {
        task: taskName,
        role: estimation.estimation_role || '',
        description: '',
        estimation: estimation.estimation_estimate || 0,
        total: estimation.total_minutes,
        cost: estimation.estimated_cost_for_customer,
        isSubtotal: false,
        isTaskStart: isFirstRowForTask,
        isRoleStart: isFirstRowForRole,
        taskRowspan: isFirstRowForTask ? taskRowCount : undefined,
        roleRowspan: isFirstRowForRole ? roleRowCount : undefined
      };
    },

    createEmptyTaskRow(/** @type {string} */ taskName, /** @type {number} */ taskRowCount) {
      return {
        task: taskName,
        description: '',
        role: '',
        employee: '',
        estimation: 0,
        total: 0,
        cost: 0,
        isSubtotal: false,
        isTaskStart: true,
        isRoleStart: true,
        taskRowspan: taskRowCount,
        roleRowspan: 1
      };
    },

    createSubtotalRow(/** @type {{ estimation_estimate: number; total: number; total_cost: number }} */ subtotal) {
      return {
        task: 'Subtotal',
        description: '',
        role: '',
        employee: '',
        estimation: subtotal.estimation_estimate,
        total: subtotal.total,
        cost: subtotal.total_cost,
        isSubtotal: true,
        isTaskStart: false,
        isRoleStart: false,
        taskRowspan: undefined,
        roleRowspan: undefined
      };
    },
  },
};
</script>

<style lang="less">
@import "~variables";

.sow-type-table {
  overflow: auto;

  .sow-type-table__header {
    &-task {
      min-width: 200px;
      width: 200px;
    }
    &-role {
      min-width: 110px;
      width: 110px;
    }
    &-description {
      min-width: 200px;
      width: 200px;
    }
    &-employee {
      min-width: 150px;
      width: 150px;
    }
    &-estimation {
      min-width: 130px;
      width: 130px;
    }
    &-total {
      min-width: 130px;
      width: 130px;
    }
    &-cost {
      min-width: 130px;
      width: 130px;
    }
  }

  .total-background {
    background-color: #e0e0e0;
    font-weight: bold;
  }

  tr[data-task-start="true"] {
    border-top: 2px solid #e0e0e0;
  }

  &__header {
    background-color: #f5f5f5;
    font-weight: bold;
  }
}
</style>
